/**
 * 🌟 Inflomatrix 2.0 App - Living Digital Organism
 *
 * Host·Path planes (D14): inflomatrix.app → Platform; {tenant}.* → Site + Business
 */

import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { capturePageView } from '@/observability/posthog';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './tenant/business/frontend/utils/queryClient';
import { App as AntApp, Spin, type ThemeConfig } from 'antd';
import {
  AntdLocaleBridge,
  LocaleProvider,
} from '@shared/internationalization-locale-and-multilingual-experience-foundation';
import { createAntdThemeFromCssVars } from './tenant/business/frontend/config/antdTheme';
import { config } from './tenant/business/frontend/config/environment';
import { TenantProvider } from './tenant/business/frontend/contexts/TenantContext';
import { resolveHostContext } from '@shared/tenant-host';
import { TenantHostRouter } from './tenant/frontend/TenantHostRouter';
import { logger } from './shared/logger';
import { AppErrorBoundary } from './tenant/business/frontend/components/common/AppErrorBoundary';
import { registerDocumentExecutorPorts } from './tenant/business/frontend/services/registerDocumentExecutors';
import './index.css';
import './tenant/business/frontend/styles/inflomatrix-dark.css';
import './tenant/business/frontend/styles/business-a11y.css';

const PlatformRouter = React.lazy(() =>
  import('./tenant/platform/frontend/router/PlatformRouter').then((mod) => ({
    default: mod.PlatformRouter,
  }))
);

const RouterLoadingFallback: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'var(--grad-deep)',
    }}
  >
    <Spin size="large" aria-label="라우터 로딩" />
  </div>
);

const initializeAuth = async () => {};
const initializeTheme = () => {};
const setCSRFToken = () => {};

const PostHogPageViewTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    capturePageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

const App: React.FC = () => {
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [antdTheme, setAntdTheme] = React.useState<ThemeConfig>(() => createAntdThemeFromCssVars());

  useEffect(() => {
    setAntdTheme(createAntdThemeFromCssVars());

    const initialize = async () => {
      try {
        logger.info('🚀 Inflomatrix Frontend 초기화 시작...');
        initializeTheme();
        setCSRFToken();
        registerDocumentExecutorPorts();
        await initializeAuth();
        setIsInitialized(true);
        logger.info('🎉 Inflomatrix Frontend 초기화 완료!');
      } catch (error) {
        logger.error('❌ 초기화 실패', error instanceof Error ? error : undefined, { error });
        setIsInitialized(true);
      }
    };

    void initialize();
  }, []);

  if (!isInitialized) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--grad-deep)',
        }}
      >
        <div style={{ textAlign: 'center', color: 'var(--color-text-soft)' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 18 }}>🌟 Inflomatrix 초기화 중...</div>
        </div>
      </div>
    );
  }

  const tenantRootDomain = import.meta.env.VITE_TENANT_ROOT_DOMAIN as string | undefined;
  const hostContext = resolveHostContext({
    hostname: window.location.hostname,
    rootDomain: tenantRootDomain,
    devFallbackTenantId: config.tenant.defaultTenantId,
  });
  const localePlane =
    hostContext.kind === 'platform' ? ('platform' as const) : ('business' as const);

  return (
    <AppErrorBoundary>
      <StrictModeWrapper>
        <QueryClientProvider client={queryClient}>
          <LocaleProvider
            plane={localePlane}
            tenantId={
              hostContext.kind === 'platform'
                ? null
                : (hostContext.tenantId ?? config.tenant.defaultTenantId)
            }
          >
            {/* Antd locale follows SupportedLocale; theme remains constitutional dark baseline. */}
            <AntdLocaleBridge theme={antdTheme}>
              <AntApp>
                <BrowserRouter>
                  <PostHogPageViewTracker />
                  <Suspense fallback={<RouterLoadingFallback />}>
                    {hostContext.kind === 'platform' ? (
                      <PlatformRouter />
                    ) : (
                      <TenantProvider
                        initialTenantId={hostContext.tenantId ?? config.tenant.defaultTenantId}
                      >
                        <TenantHostRouter tenantRootDomain={hostContext.rootDomain} />
                      </TenantProvider>
                    )}
                  </Suspense>
                </BrowserRouter>
              </AntApp>
            </AntdLocaleBridge>
          </LocaleProvider>
        </QueryClientProvider>
      </StrictModeWrapper>
    </AppErrorBoundary>
  );
};

const StrictModeWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <React.StrictMode>{children}</React.StrictMode>
);

export default App;
